import { useCallback, useEffect, useRef, useState } from 'react';

/* Coluna lateral redimensionável por um punho visível.

   A largura é uma preferência de quem trabalha na tela — não um recorte de
   trabalho — então vive no localStorage e sobrevive a fechar o navegador.

   O `encaixe` existe porque há uma largura *certa* que não é óbvia de achar no
   olho: aquela em que a prévia mostra a página no tamanho real. Perto dela o
   punho gruda. */

interface Opcoes {
  chave: string;
  padrao: number;
  minimo: number;
  maximo: number;
  /** Largura mínima que a coluna da ESQUERDA precisa manter. */
  minimoDoOutroLado: number;
  /** Largura do próprio punho, que também sai do total. */
  reserva?: number;
  /**
   * O ponto de encaixe. Número fixo, ou uma função que o mede no DOM —
   * necessário quando a conta depende de padding em `rem`: a raiz do app é
   * `clamp(13px, 1vw, 16px)`, então o mesmo layout dá contas diferentes em
   * janelas diferentes e um número fixo só acerta por acaso numa largura.
   */
  encaixe?: number | (() => number | null);
  tolerancia?: number;
  passoDoTeclado?: number;
}

function ler(chave: string, padrao: number): number {
  try {
    const v = Number(localStorage.getItem(chave));
    return Number.isFinite(v) && v > 0 ? v : padrao;
  } catch {
    return padrao;
  }
}

function gravar(chave: string, valor: number) {
  try {
    localStorage.setItem(chave, String(Math.round(valor)));
  } catch {
    /* Armazenamento bloqueado: a largura vale só nesta sessão. */
  }
}

export function useColunaRedimensionavel(opcoes: Opcoes) {
  const {
    chave, padrao, minimo, maximo, minimoDoOutroLado, reserva = 0,
    encaixe, tolerancia = 12, passoDoTeclado = 20,
  } = opcoes;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [largura, setLargura] = useState(() => ler(chave, padrao));
  const [arrastando, setArrastando] = useState(false);
  const inicio = useRef({ x: 0, largura: 0 });

  /* O teto real depende da janela: a coluna não pode comer o espaço que o
     outro lado precisa. Recalculado a cada mudança de tamanho. */
  const tetoReal = useCallback(() => {
    const total = containerRef.current?.clientWidth ?? 0;
    if (!total) return maximo;
    /* O punho também ocupa largura: sem descontá-lo aqui, o outro lado fica
       com menos do que o mínimo pedido — exatamente a largura do punho. */
    return Math.max(minimo, Math.min(maximo, total - minimoDoOutroLado - reserva));
  }, [maximo, minimo, minimoDoOutroLado, reserva]);

  const ajustar = useCallback((bruta: number, comEncaixe: boolean) => {
    let v = Math.round(bruta);
    if (comEncaixe && encaixe !== undefined) {
      const alvo = typeof encaixe === 'function' ? encaixe() : encaixe;
      if (alvo !== null && alvo !== undefined && Math.abs(v - alvo) <= tolerancia) v = Math.round(alvo);
    }
    return Math.max(minimo, Math.min(tetoReal(), v));
  }, [encaixe, minimo, tetoReal, tolerancia]);

  /* Se a janela encolher, a coluna cede — senão o editor fica espremido sem
     que ninguém tenha arrastado nada. */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      setLargura((atual) => {
        const teto = tetoReal();
        return atual > teto ? teto : atual;
      });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [tetoReal]);

  const aoApontar = (ev: React.PointerEvent<HTMLElement>) => {
    ev.preventDefault();
    (ev.target as HTMLElement).setPointerCapture(ev.pointerId);
    inicio.current = { x: ev.clientX, largura };
    setArrastando(true);
  };

  const aoMover = (ev: React.PointerEvent<HTMLElement>) => {
    if (!arrastando) return;
    /* A coluna é a da direita: arrastar para a esquerda a aumenta. */
    setLargura(ajustar(inicio.current.largura - (ev.clientX - inicio.current.x), true));
  };

  const aoSoltar = (ev: React.PointerEvent<HTMLElement>) => {
    if (!arrastando) return;
    (ev.target as HTMLElement).releasePointerCapture(ev.pointerId);
    setArrastando(false);
    gravar(chave, largura);
  };

  /* Quem não arrasta com o mouse também alcança. */
  const aoTeclar = (ev: React.KeyboardEvent<HTMLElement>) => {
    const delta = ev.key === 'ArrowLeft' ? passoDoTeclado : ev.key === 'ArrowRight' ? -passoDoTeclado : 0;
    if (!delta) return;
    ev.preventDefault();
    const nova = ajustar(largura + delta, false);
    setLargura(nova);
    gravar(chave, nova);
  };

  return {
    containerRef,
    largura,
    arrastando,
    maximoAtual: tetoReal(),
    minimo,
    /* Pronto para espalhar no elemento do punho. */
    propsDoPunho: {
      role: 'separator' as const,
      tabIndex: 0,
      'aria-orientation': 'vertical' as const,
      'aria-valuenow': largura,
      'aria-valuemin': minimo,
      'aria-valuemax': tetoReal(),
      onPointerDown: aoApontar,
      onPointerMove: aoMover,
      onPointerUp: aoSoltar,
      onPointerCancel: aoSoltar,
      onKeyDown: aoTeclar,
    },
  };
}
